import React, { useState } from 'react';
import { X, Copy, Check, QrCode, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { QRCodeSVG } from 'qrcode.react';

interface ExpansionOrderData {
  orderId: number;
  amount: number;
  systemNote: string;
  paymentAddress: string;
  contractAddress: string;
  expiredAt: string;
  spaceAddedDesc: string;
}

interface StorageExpansionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const StorageExpansionModal: React.FC<StorageExpansionModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [selectedSpace, setSelectedSpace] = useState<500 | 1024>(500);
  const [step, setStep] = useState<'select' | 'waiting' | 'success'>('select');
  const [orderData, setOrderData] = useState<ExpansionOrderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedNote, setCopiedNote] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [txhash, setTxhash] = useState<string | null>(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentCheckInterval, setPaymentCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const resetState = () => {
    setSelectedSpace(500);
    setStep('select');
    setOrderData(null);
    setLoading(false);
    setCopiedAddress(false);
    setCopiedNote(false);
    setShowQr(false);
    setTxhash(null);
    if (paymentCheckInterval) {
      clearInterval(paymentCheckInterval);
      setPaymentCheckInterval(null);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const createOrder = async () => {
    console.log('开始创建订单, selectedSpace:', selectedSpace);
    try {
      setLoading(true);
      console.log('发送请求到 /reconciliation/order/expansion');
      const response = await api.post<any>('/reconciliation/order/expansion', {
        spaceAmount: selectedSpace
      });
      console.log('收到响应:', response);

      if (response?.success && response?.data) {
        console.log('订单创建成功，准备设置订单数据');
        setOrderData(response.data);
        setStep('waiting');
        startPaymentMonitoring();
      } else {
        console.log('订单创建失败:', response?.message);
        toast.error(response?.message || '创建订单失败');
      }
    } catch (error: any) {
      console.error('创建订单异常:', error);
      toast.error(error.response?.data?.message || error.message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.paymentAddress);
        setCopiedAddress(true);
        toast.success('地址已复制');
        setTimeout(() => setCopiedAddress(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.paymentAddress;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedAddress(true);
          toast.success('地址已复制');
          setTimeout(() => setCopiedAddress(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const copyNote = async () => {
    if (!orderData) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(orderData.systemNote);
        setCopiedNote(true);
        toast.success('备注已复制');
        setTimeout(() => setCopiedNote(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = orderData.systemNote;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopiedNote(true);
          toast.success('备注已复制');
          setTimeout(() => setCopiedNote(false), 2000);
        } else {
          toast.error('复制失败，请手动复制');
        }
      }
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  const getTimeRemaining = () => {
    if (!orderData) return null;
    const now = new Date().getTime();
    const expired = new Date(orderData.expiredAt).getTime();
    const diff = expired - now;
    if (diff <= 0) return '已过期';

    const minutes = Math.floor((diff / 1000 / 60) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const checkUsdtTransaction = async (): Promise<{ found: boolean; txid?: string; fromAddress?: string }> => {
    if (!orderData) return { found: false };

    try {
      const address = orderData.paymentAddress;
      const expectedAmount = orderData.amount.toString();
      const expectedNote = orderData.systemNote;

      const url = `https://api.trongrid.io/v1/accounts/${address}/transactions/trc20?contract_address=TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t&limit=20`;

      const response = await fetch(url);
      if (!response.ok) return { found: false };

      const data = await response.json();

      if (data.data && data.data.length > 0) {
        const currentTime = Date.now();
        const matchingTx = data.data.find((tx: any) => {
          if (tx.to !== address) return false;

          const txAmount = (parseInt(tx.value) / 1e6).toFixed(6);
          const expected = parseFloat(expectedAmount).toFixed(6);

          if (Math.abs(parseFloat(txAmount) - parseFloat(expected)) >= 0.000001) return false;

          const blockTime = tx.block_timestamp;
          if (!blockTime || currentTime - blockTime < 60000) return false;

          return true;
        });

        if (matchingTx) {
          return {
            found: true,
            txid: matchingTx.transaction_id,
            fromAddress: matchingTx.from
          };
        }
      }

      return { found: false };
    } catch (error) {
      console.error('查询USDT交易失败:', error);
      return { found: false };
    }
  };

  const confirmOrderToBackend = async (txid: string, fromAddress: string): Promise<boolean> => {
    try {
      const response = await api.post<any>('/reconciliation/order/confirm-expansion', {
        orderId: orderData!.orderId,
        txid,
        fromAddress
      });
      return response?.success === true;
    } catch (error) {
      console.error('确认订单失败:', error);
      return false;
    }
  };

  const finalizeOrderOnBackend = async (): Promise<boolean> => {
    try {
      const response = await api.post<any>('/reconciliation/order/finalize-expansion', {
        orderId: orderData!.orderId
      });
      return response?.success === true;
    } catch (error) {
      console.error('完成订单失败:', error);
      return false;
    }
  };

  const startPaymentMonitoring = () => {
    if (paymentCheckInterval) return;

    toast.success('开始监控USDT支付，请在30分钟内完成支付');

    const interval = setInterval(async () => {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        toast.success('检测到支付，准备确认订单...');

        const confirmed = await confirmOrderToBackend(result.txid, result.fromAddress || '');
        if (confirmed) {
          toast.success('链上确认中，等待2个区块确认...');
          setTimeout(async () => {
            const finalized = await finalizeOrderOnBackend();
            if (finalized) {
              toast.success('支付成功！空间已添加到您的账户');
              setStep('success');
              setTimeout(() => {
                onSuccess();
                handleClose();
              }, 2000);
            } else {
              toast.error('订单已完成，但更新容量失败，请联系客服');
            }
          }, 15000);
        } else {
          toast.error('确认订单失败，请重试');
        }
      }
    }, 10000);

    setPaymentCheckInterval(interval);

    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPaymentCheckInterval(null);
        if (orderData && !txhash) {
          toast.error('支付超时，请重新下单');
          handleClose();
        }
      }
    }, 30 * 60 * 1000);
  };

  const handleManualCheck = async () => {
    setCheckingPayment(true);
    try {
      const result = await checkUsdtTransaction();
      if (result.found && result.txid) {
        toast.success('检测到支付，准备确认订单...');

        const confirmed = await confirmOrderToBackend(result.txid, result.fromAddress || '');
        if (confirmed) {
          toast.success('链上确认中，等待2个区块确认...');
          setTimeout(async () => {
            const finalized = await finalizeOrderOnBackend();
            if (finalized) {
              toast.success('支付成功！空间已添加到您的账户');
              setStep('success');
              setTimeout(() => {
                onSuccess();
                handleClose();
              }, 2000);
            } else {
              toast.error('订单已完成，但更新容量失败，请联系客服');
            }
          }, 15000);
        } else {
          toast.error('确认订单失败，请重试');
        }
      } else {
        toast.error('未检测到支付，请确认已转账');
      }
    } finally {
      setCheckingPayment(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">EA网盘扩容</h2>
          <button
            onClick={handleClose}
            className="p-1 text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* 步骤1：选择套餐 */}
          {step === 'select' && (
            <div>
              <div className="text-sm text-gray-500 mb-4">
                当前免费空间：100MB，仅支持TRC20-USDT支付
              </div>

              <div className="space-y-3">
                <div
                  onClick={() => setSelectedSpace(500)}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedSpace === 500
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-800">+500MB 空间</div>
                      <div className="text-sm text-gray-500 mt-1">适合小文件存储</div>
                    </div>
                    <div className="text-pink-600 font-bold text-xl">10.00 USDT</div>
                  </div>
                </div>

                <div
                  onClick={() => setSelectedSpace(1024)}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedSpace === 1024
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-800">+1GB 空间</div>
                      <div className="text-sm text-gray-500 mt-1">适合大量文件存储</div>
                    </div>
                    <div className="text-pink-600 font-bold text-xl">50.00 USDT</div>
                  </div>
                </div>
              </div>

              <button
                onClick={createOrder}
                disabled={loading}
                className="w-full mt-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-colors disabled:opacity-50"
              >
                {loading ? '创建订单中...' : '生成订单'}
              </button>
            </div>
          )}

          {/* 步骤2：等待支付 */}
          {step === 'waiting' && orderData && (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="text-gray-600 mb-2">请完成以下转账</div>
                <div className="text-3xl font-bold text-pink-600">
                  {orderData.amount.toFixed(6)} USDT
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  获得 {orderData.spaceAddedDesc}
                </div>
                <div className="text-sm text-red-500 mt-1">
                  剩余时间：{getTimeRemaining()}
                </div>
              </div>

              {/* 收款地址 */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">收款地址（TRC20）</div>
                <div className="bg-gray-100 rounded p-2 text-sm break-all mb-2">
                  {orderData.paymentAddress}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyAddress}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-pink-500 text-white rounded hover:bg-pink-600 transition-colors text-sm"
                  >
                    {copiedAddress ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copiedAddress ? '已复制' : '复制地址'}
                  </button>
                  <button
                    onClick={() => setShowQr(!showQr)}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors text-sm"
                  >
                    <QrCode className="w-4 h-4" />
                    {showQr ? '隐藏' : '二维码'}
                  </button>
                </div>
              </div>

              {/* 二维码 */}
              {showQr && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 border border-pink-200 rounded-lg">
                    <QRCodeSVG value={orderData.paymentAddress} size={192} level="H" />
                    <div className="w-48 h-48 flex items-center justify-center bg-gray-100">
                      <div className="text-center text-gray-500 text-xs">二维码</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 系统备注 */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="text-sm text-gray-600 mb-2">系统备注（必填）</div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-sm break-all mb-2">
                  {orderData.systemNote}
                </div>
                <button
                  onClick={copyNote}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors text-sm"
                >
                  {copiedNote ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copiedNote ? '已复制' : '复制备注'}
                </button>
              </div>

              {/* 重要提示 */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="text-sm font-medium text-yellow-800 mb-2">
                  ⚠️ 重要提示
                </div>
                <ul className="text-xs text-yellow-800 space-y-1">
                  <li>• 请使用 TRC20 网络，选错网络资产无法找回</li>
                  <li>• 转账金额必须与显示完全一致</li>
                  <li>• 必须复制系统生成备注并填写在备注中</li>
                  <li>• 链上确认后会自动同步订单状态</li>
                </ul>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleManualCheck}
                  disabled={checkingPayment}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${checkingPayment ? 'animate-spin' : ''}`} />
                  {checkingPayment ? '检查中...' : '手动检查'}
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  关闭
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                  自动监控中，确认后需等待2个区块确认
                </div>
              </div>
            </div>
          )}

          {/* 步骤3：支付成功 */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <div className="text-2xl font-bold text-green-600 mb-2">支付成功！</div>
              <div className="text-gray-600">正在同步订单...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StorageExpansionModal;
